import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Folder, FileText, Check, ChevronRight, Loader2, HardDriveDownload, Home,
} from "lucide-react";

const LEVELS = ["all", "A1", "A2", "B1", "B2", "C1"];

/** Picks files from a Drive folder shared with the school account and copies
 *  them into Recursos (one resource per file). The original is never touched. */
export const DriveImportDialog = ({ open, onOpenChange, onImported, isAdmin }) => {
  const [data, setData] = useState(null);
  const [trail, setTrail] = useState([]);
  const [picked, setPicked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rootUrl, setRootUrl] = useState("");
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ category: "", subcategory_id: "", visibility: "all", level: "all" });
  const [useFolderAsSub, setUseFolderAsSub] = useState(true);

  // The school organises the Drive as "Nivel A1 → Tema": read the level from the
  // breadcrumb so it does not have to be picked by hand on every folder.
  const detectLevel = (crumbs) => {
    for (const c of [...crumbs].reverse()) {
      const m = (c.name || "").toUpperCase().match(/\b([ABC][12])\b/);
      if (m) return m[1];
    }
    return "all";
  };

  const browse = async (folderId, pushName) => {
    setLoading(true);
    try {
      const r = await api.get("/drive-import/browse", { params: folderId ? { folder_id: folderId } : {} });
      setData(r.data);
      setPicked([]);
      const crumbs = pushName ? [...trail, { id: folderId, name: pushName }] : trail;
      if (pushName) setTrail(crumbs);
      setForm((f) => ({ ...f, level: detectLevel(crumbs) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer la carpeta");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setTrail([]);
    browse(null);
    api.get("/resource-categories").then((r) => {
      const list = r.data || [];
      setCats(list);
      const first = list.find((c) => !c.parent_id);
      setForm((f) => ({ ...f, category: f.category || first?.name || "" }));
    }).catch(() => {});
    /* eslint-disable-next-line */
  }, [open]);

  const saveRoot = async () => {
    setBusy(true);
    try {
      const r = await api.put("/drive-import/root", { url: rootUrl });
      toast.success(`Carpeta conectada: ${r.data.folder_name}`);
      await browse(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo conectar la carpeta");
    } finally {
      setBusy(false);
    }
  };

  const goTo = (idx) => {
    const target = idx < 0 ? null : trail[idx].id;
    setTrail(trail.slice(0, idx + 1));
    browse(target);
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const r = await api.post("/drive-import/run", {
        file_ids: picked,
        category: form.category,
        subcategory_id: form.subcategory_id || null,
        subcategory_name: !form.subcategory_id && useFolderAsSub && trail.length
          ? trail[trail.length - 1].name.replace(/^\d+\.\s*/, "")
          : null,
        visibility: form.visibility,
        level: form.level,
      });
      const n = r.data.created.length;
      toast.success(
        `${n} recurso${n === 1 ? "" : "s"} creado${n === 1 ? "" : "s"}` +
        (r.data.skipped ? ` · ${r.data.skipped} ya estaban` : "") +
        (r.data.failed ? ` · ${r.data.failed} fallaron` : "") +
        (r.data.pending_review ? " · quedan pendientes de aprobación" : "")
      );
      onImported?.();
      await browse(trail.length ? trail[trail.length - 1].id : null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar");
    } finally {
      setBusy(false);
    }
  };

  const subs = cats.filter((c) => {
    const parent = cats.find((p) => !p.parent_id && p.name === form.category);
    return parent && c.parent_id === parent.id;
  });
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const files = data?.files || [];
  const selectable = files.filter((f) => !f.imported);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="drive-import-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardDriveDownload className="h-4 w-4" /> Importar desde Drive
          </DialogTitle>
        </DialogHeader>

        {data?.needs_setup ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Compartí una carpeta de tu Drive con la cuenta de la escuela (como Editor) y pegá su enlace acá.
              Solo hay que hacerlo una vez: después podés seguir agregando archivos a esa carpeta y aparecen solos.
            </p>
            <Input value={rootUrl} onChange={(e) => setRootUrl(e.target.value)}
                   placeholder="https://drive.google.com/drive/folders/..."
                   data-testid="drive-import-root-input" />
            {!isAdmin && <p className="text-xs text-amber-700">Solo un admin puede conectar la carpeta.</p>}
            <Button onClick={saveRoot} disabled={busy || !rootUrl.trim() || !isAdmin}
                    data-testid="drive-import-root-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conectar carpeta"}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1 text-xs text-slate-500 flex-wrap">
              <button type="button" onClick={() => goTo(-1)} className="flex items-center gap-1 hover:text-brand"
                      data-testid="drive-import-crumb-root">
                <Home className="h-3.5 w-3.5" /> {data?.root_name || "Carpeta"}
              </button>
              {trail.map((c, i) => (
                <span key={c.id} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  <button type="button" onClick={() => goTo(i)} className="hover:text-brand">{c.name}</button>
                </span>
              ))}
            </div>

            <Card className="max-h-[38vh] overflow-y-auto divide-y divide-slate-50" data-testid="drive-import-list">
              {loading && <div className="p-6 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>}
              {!loading && (data?.folders || []).map((f) => (
                <button type="button" key={f.id} onClick={() => browse(f.id, f.name)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50"
                        data-testid={`drive-import-folder-${f.id}`}>
                  <Folder className="h-4 w-4 text-sky-500 shrink-0" />
                  <span className="flex-1 text-sm text-slate-800 truncate">{f.name}</span>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              ))}
              {!loading && files.map((f) => {
                const on = picked.includes(f.id);
                return (
                  <button type="button" key={f.id} disabled={f.imported} onClick={() => toggle(f.id)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left ${f.imported ? "opacity-50" : on ? "bg-brand/10" : "hover:bg-slate-50"}`}
                          data-testid={`drive-import-file-${f.id}`}>
                    <span className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center ${on ? "bg-brand border-brand" : "border-slate-300"}`}>
                      {(on || f.imported) && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="flex-1 text-sm text-slate-800 truncate">{f.name}</span>
                    {f.imported && <span className="text-[10px] font-bold uppercase text-slate-400">ya importado</span>}
                  </button>
                );
              })}
              {!loading && !files.length && !(data?.folders || []).length && (
                <div className="p-8 text-center text-sm text-slate-500">Esta carpeta está vacía</div>
              )}
            </Card>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, subcategory_id: "" })}>
                  <SelectTrigger data-testid="drive-import-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.filter((c) => !c.parent_id).map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Subcategoría</Label>
                <Select value={form.subcategory_id || "none"} onValueChange={(v) => setForm({ ...form, subcategory_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="drive-import-subcategory"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin subcategoría</SelectItem>
                    {subs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Visibilidad</Label>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger data-testid="drive-import-visibility"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="staff">Solo profesores</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Nivel</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="drive-import-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{l === "all" ? "Todos" : l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Se copia cada archivo al Drive de la escuela y se crea un recurso por archivo, con el nombre
              del archivo como título. Tu original queda intacto.
            </p>
            {!form.subcategory_id && trail.length > 0 && (
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={useFolderAsSub} onChange={(e) => setUseFolderAsSub(e.target.checked)}
                       data-testid="drive-import-folder-as-sub" />
                Crear la subcategoría <strong>{trail[trail.length - 1].name.replace(/^\d+\.\s*/, "")}</strong> con el nombre de esta carpeta
              </label>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setPicked(selectable.map((f) => f.id))}
                      disabled={!selectable.length} data-testid="drive-import-select-all">
                Seleccionar todo
              </Button>
              <Button onClick={runImport} disabled={busy || !picked.length || !form.category}
                      data-testid="drive-import-run">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Importar ${picked.length || ""}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DriveImportDialog;
